import { Button, Flex, Spin, Typography } from 'antd'
import React, { useEffect, useState } from 'react'

import { CouponService } from '@services/CouponService'
import { ProductService } from '@services/ProductService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { Coupon, Product } from '../../../types'

import { AddCouponModal } from './components/AddCouponModal'
import { CouponTable } from './components/CouponTable'
import { EditCouponModal } from './components/EditCouponModal'

export const CouponsDashboardPage = () => {
    const [coupons, setCoupons] = useState<Coupon[]>()
    const [products, setProducts] = useState<Product[]>([])
    const [selectedCoupon, setSelectedCoupon] = useState<Coupon | undefined>()
    const [isLoading, setIsLoading] = useState(false)
    const [showEditModal, setShowEditModal] = useState<boolean>(false)
    const [showAddModal, setShowAddModal] = useState<boolean>(false)
    const { showNotification } = useNotifications()
    const confirm = useConfirmation()

    const list = async () => {
        setIsLoading(true)
        try {
            const results = await CouponService.list()
            setCoupons(results)
        } catch (err) {
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchProducts = async () => {
        try {
            const results = await ProductService.list()
            setProducts(results)
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        fetchProducts()
        list()
    }, [])

    const onDelete = async (id: string) => {
        try {
            if (
                await confirm({
                    title: 'Are you sure you want to delete this coupon?',
                    description: 'Users will no longer be able to redeem this coupon',
                    confirmType: 'danger'
                })
            ) {
                setIsLoading(true)
                await CouponService.remove(id)
                showNotification({ title: 'Coupon successully deleted' })
                await list()
            }
        } catch (err: any) {
            const message = err.response.data ? err.response.data : err.toString()
            showNotification({ title: 'Could not delete the coupon', type: 'error', description: message })
        } finally {
            setIsLoading(false)
        }
    }

    if (isLoading || !coupons) {
        return <Spin />
    }

    return (
        <>
            <Flex justify="space-between" align="center">
                <Typography.Title>Coupons</Typography.Title>
                <Button type="primary" onClick={() => setShowAddModal(true)}>
                    + Create Coupon
                </Button>
            </Flex>

            <CouponTable
                coupons={coupons}
                onEdit={coupon => {
                    setSelectedCoupon(coupon)
                    setShowEditModal(true)
                }}
                onDelete={onDelete}
            />
            {showEditModal && selectedCoupon && (
                <EditCouponModal
                    coupon={selectedCoupon}
                    isOpen={showEditModal}
                    onClose={() => {
                        setSelectedCoupon(undefined)
                        setShowEditModal(false)
                    }}
                    onSuccess={list}
                    availableProducts={products}
                />
            )}
            {showAddModal && (
                <AddCouponModal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false)
                    }}
                    onSuccess={list}
                    availableProducts={products}
                />
            )}
        </>
    )
}
